import { TitleCasePipe } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
    ViewChild
} from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';

import moment from 'moment-timezone';
import { MessageService } from 'primeng/api';
import { BehaviorSubject, Subscription, NEVER } from 'rxjs';
import { distinctUntilChanged, filter, catchError, switchMap, tap, take } from 'rxjs/operators';

import { MediaServiceApi } from '../../../../api/mediaService/media-service.api';
import { MediaTypeEnum } from '@Common/Basic/enums/media-type.enum';
import { MentionsApi } from '../../../../api/mentions/mentions.api';
import { PartialMentionApiModel } from '../../../../api/mentions/model/mention.model';
import { ProminenceRecalculationJobStatus } from '../../../../api/monitoring/monitoring-account-configuration.api';
import { IndexHighlightPipe } from '../../../Misc/pipes/Highlight/indexHighlight.pipe';
import { ConfigService } from '../../../../Core/Config/services/config.service';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { AccountModel } from '../../../../Core/Users/models/account.model';
import { PreferencesModel } from '../../../../Core/Users/models/preferences.model';
import { UserModel } from '../../../../Core/Users/models/user.model';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { FolderService } from '../../../../Monitor/Core/services/folder.service';
import { MentionsService } from '../../../../Monitor/Core/services/mentions.service';
import { TopicsService } from '../../../../Monitor/Core/services/topics.service';
import { EngagementAttributeTypesEnum } from '../../../../Monitor/Shared/enums/engagement-attribute-types.enum';
import { mentionAttributeTypesEnum } from '../../../../Monitor/Shared/enums/mention-attribute-types.enum';
import { Folder } from '../../../../Monitor/Shared/models/folder.model';
import { MentionSearchModel } from '../../../../Monitor/Shared/models/mention-search.model';
import { ProminenceLevel, ProminenceScoreModel } from '../../../../Monitor/Shared/models/prominence-score.model';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { JournodexGeographyModel } from '../../../Journodex/models/journodex-geography.model';
import { attributeTypesEnum } from '../../../Misc/enums/attribute-types.enum';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { AttributeModel } from '../../../Misc/models/attribute.model';
import { KeywordHighlightModel } from '../../../Misc/models/keyword-highlight.model';
import { ProminenceLockerService } from '../../../Misc/services/prominence-locker.service';
import { TagsService } from '../../../Tags/services/tags.service';
import { Mention } from '../../models/mention';
import { TwitterSubTypeEnum } from '@Common/Basic/enums/twitter-sub-type.enum';
import { TranslateMentionsService } from '../../services/translate-mentions.service';
import { MentionInsightModel } from '../../models/mention-insight.model';
import { ResourceActionTypes } from '../../../Basic/enums/resource-actions.enum';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { MentionWorkbenchDialogComponent, MentionWorkbenchTabIds } from '../MentionWorkbenchDialog/mention-workbench-dialog.component';
import { ManageMentionOrganizationDialogComponent } from '../ManageMentionOrganization/manage-mention-organization-dialog.component';
import { ProminenceTakeOverPanelComponent }
    from '../../../../Account/AccountPreferences/components/ProminenceTakeOverPanel/prominence-take-over-panel.component';


@Component({
    selector: 'agpr-mention-information',
    templateUrl: './mention-information.component.html',
    styleUrls: ['./mention-information.component.scss'],
    providers: [{
        provide: MediaServiceApi,
        useClass: MediaServiceApi
    }],
    standalone: false
})
export class MentionInformationComponent implements OnDestroy, OnInit {

    @Input() id: string;
    @Input() highlightKeywords: KeywordHighlightModel[];
    @Input() hasProminenceProvision: boolean;
    @Input() showInsights: boolean = false;

    @Output() outletClicked: EventEmitter<void> = new EventEmitter<void>();
    @Output() contactClicked: EventEmitter<void> = new EventEmitter<void>();

    @ViewChild('prominenceAttributeTemplate') prominenceAttributeTemplate: TemplateRef<any>;

    overlayContainer: Element;
    preferences: PreferencesModel;
    account: AccountModel;
    user: UserModel;
    mention: Mention = null;
    mentionAttributes: AttributeModel[] = [];
    mentionInformation: AttributeModel[] = [];
    engagementInformation: AttributeModel[] = [];
    mentionProminence: AttributeModel[] = [];
    dateFormat: string;

    notesParagraphs: string[] = [];

    folders: Folder[] = [];
    subscriptions: Subscription[] = [];

    search: MentionSearchModel;

    lexisNexusResourceUrl: SafeResourceUrl;

    prominenceStatus: ProminenceRecalculationJobStatus;
    noProminenceContactLink: string;

    insights: MentionInsightModel[] = [];

    isResellerAccount: boolean = false;

    private hasExpiryDateAttribute: boolean;
    private regionRequest: Subscription;
    private initialTagIds: number[];

    readonly messageSeverityEnum = messageSeverityEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly prominenceStatuses = ProminenceRecalculationJobStatus;
    readonly endOfUrl = /([^\/]+$)/g;
    readonly tweetTypeEnum = TwitterSubTypeEnum;

    private readonly expiryDateFormat = 'MMMM D, YYYY';
    private readonly totalValidDaysForCriticalMention: number = 60;

    readonly mentionAttributeTypesEnum = mentionAttributeTypesEnum;
    readonly attributeTypesEnum = attributeTypesEnum;

    constructor(
        private configurationService: ConfigService,
        public mentionsApi: MentionsApi,
        private folderService: FolderService,
        private messageService: MessageService,
        private localizationService: LocalizationService,
        private mentionsService: MentionsService,
        private coreDialogService: CoreDialogService,
        private currentUserStateService: CurrentUserStateService,
        private topicsService: TopicsService,
        private tagsService: TagsService,
        public httpClient: HttpClient,
        private prominenceLockerService: ProminenceLockerService,
        private cdr: ChangeDetectorRef,
        protected indexHighlightPipe: IndexHighlightPipe,
        protected translateService: TranslateMentionsService,
        public injector: Injector
    ) {
        this.isResellerAccount = configurationService.hasBrandGUID();
        this.overlayContainer = document.getElementById('main-content');
    }

    ngOnInit(){
        this.createSubscriptions();
        this.setNoProminenceContactLink();
    }

    /**
     * Removes the mention from the provided folder
    */
    public removeMentionFromFolder(folder: Folder) {
        if (this.mention) {
            this.folderService.removeMentionsFromFolder([this.mention], folder).subscribe();
        }
    }

    private createSubscriptions(): void {
        this.subscriptions.push(
            this.prominenceLockerService.prominenceStatus$.subscribe(status => this.prominenceStatus = status),
            this.mentionsService.getFocusedMention().pipe(
                switchMap((focusedMention) => this.folderService.getFoldersByMention(focusedMention)),
                filter(x => !!x)
            ).subscribe(folders => {
                this.folders = folders;
                this.folders.sort(function(folder1, folder2) {
                    const timestamp1 = +(folder1.modifiedDate ? folder1.modifiedDate : folder1.createdDate);
                    const timestamp2 = +(folder2.modifiedDate ? folder2.modifiedDate : folder2.createdDate);

                    return timestamp2 - timestamp1;
                });
            }),
            this.mentionsService.getFocusedMention().pipe(distinctUntilChanged()).subscribe(
                mention => {
                    if (!mention) {
                        this.mention = null;
                        this.lexisNexusResourceUrl = null;
                        this.notesParagraphs = [];
                        this.initialTagIds = null;
                        this.insights = [];
                    } else {
                        if (!this.mention || mention.id !== this.mention.id) {
                            this.trackMention(mention);
                        }
                        if (!this.mention || this.mention.url !== mention.url) {
                            this.cdr.detectChanges();
                        }
                        this.mention = mention;
                        this.notesParagraphs = (mention.note || '').split('\n').filter(x => x !== '');
                        this.initialTagIds = [...mention.tagIds];
                        this.setAttributes();

                        if (this.mention.insights?.length) {
                            this.insights = [...this.mention.insights];
                            this.insights.sort((a, b) => {
                                if (a.isPreferred) {
                                    return -1;
                                }

                                if(b.isPreferred) {
                                    return 1;
                                }

                                return 0;
                            });
                        } else {
                            this.insights = [];
                        }
                    }
                }
            ),
            this.topicsService.getFocusedTopic().subscribe(
                search => {
                    this.search = search;
                }
            ),
            this.localizationService.getLocalizationMapChange().subscribe(() => this.setAttributes()),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(
                prefs => {
                    this.preferences = prefs;
                    this.dateFormat = prefs.getDateFormatString(true, true, true);

                    if (this.mentionInformation && this.mentionInformation.length) {
                        this.addExpiryDateAttribute();
                    }
                }
            ),
            this.currentUserStateService.getAccount().subscribe(
                account => {
                    this.account = account;
                }
            ),
            this.currentUserStateService.getUser().subscribe(
                user => {
                    this.user = user;
                }
            ),
        );
    }

    setAttributes() {
        if (!this.mention) {
            this.mentionAttributes = [];
            return;
        }

        this.setMentionInformationAttributes();
        this.setEngagementAttributes();

        if (this.hasProminenceProvision) {
            this.setMentionProminenceAttributes();
        }

        if (!this.mention.createdDate) {
            this.mentionAttributes = [];
            return;
        }

        const createdByUserAttribute: AttributeModel = {
            id: mentionAttributeTypesEnum.savedBy,
            label: 'Mention.details.savedBy',
            value: this.mention.createdByUserId,
            type: attributeTypesEnum.user,
        };

        const createdOnDateAttribute: AttributeModel = {
            id: mentionAttributeTypesEnum.savedDate,
            label: 'Mention.details.savedDate',
            value: this.mention.createdDate,
            type: attributeTypesEnum.date,
        };

        const lastModifiedByUserAttribute: AttributeModel = {
            id: mentionAttributeTypesEnum.lastModifiedBy,
            label: 'Mention.details.modifiedBy',
            value: this.mention.lastModifiedByUserId,
            type: attributeTypesEnum.user
        };

        const lastModifiedDateAttribute: AttributeModel = {
            id: mentionAttributeTypesEnum.lastModifiedDate,
            label: 'Mention.details.modifiedDate',
            value: this.mention.lastModifiedDate,
            type: attributeTypesEnum.date,
        };

        this.mentionAttributes = [
            createdByUserAttribute,
            createdOnDateAttribute,
            lastModifiedByUserAttribute,
            lastModifiedDateAttribute,
        ].filter(x => !!x.value);
    }


    setEngagementAttributes() {
        const engagementAttribute: AttributeModel = {
            id: EngagementAttributeTypesEnum.engagements,
            label: 'Mention.details.engagements',
            value: this.mention.engagements,
            type: attributeTypesEnum.engagement,
            icon: 'fa-solid fa-retweet'
        };

        const engagementRateAttribute: AttributeModel = {
            id: EngagementAttributeTypesEnum.engagementRate,
            label: 'Mention.details.engagementRate',
            value: this.mention.engagementRate,
            type: attributeTypesEnum.percent,
            icon: 'fa-solid fa-percent'
        };

        const likeIcon = (this.mention.mediaType.id === MediaTypeEnum.FACEBOOK
            || this.mention.mediaType.id === MediaTypeEnum.INSTAGRAM
            || this.mention.mediaType.id === MediaTypeEnum.YOUTUBE
            || this.mention.mediaType.id === MediaTypeEnum.TIKTOK)
            ? 'fa-solid fa-thumbs-up'
            : 'fa-solid fa-heart';

        const likesAttribute: AttributeModel = {
            id: EngagementAttributeTypesEnum.likes,
            label: 'Mention.details.likes',
            value: this.mention.like,
            type: attributeTypesEnum.engagement,
            icon: likeIcon
        };

        const upvotesAttribute: AttributeModel = {
            id: EngagementAttributeTypesEnum.likes,
            label: 'Mention.details.upvotes',
            value: this.mention.like,
            type: attributeTypesEnum.engagement,
            icon: 'fa-solid fa-thumbs-up'
        };

        const commentsAttribute: AttributeModel = {
            id: EngagementAttributeTypesEnum.comments,
            label: 'Mention.details.comments',
            value: this.mention.reply,
            type: attributeTypesEnum.engagement,
            icon: 'fa-solid fa-comment'
        };

        const repliesAttribute: AttributeModel = {
            id: EngagementAttributeTypesEnum.comments,
            label: 'Mention.details.replies',
            value: this.mention.reply,
            type: attributeTypesEnum.engagement,
            icon: 'fa-solid fa-comment'
        };

        const sharesAttribute: AttributeModel = {
            id: EngagementAttributeTypesEnum.shares,
            label: 'Mention.details.shares',
            value: this.mention.share,
            type: attributeTypesEnum.engagement,
            icon: 'fa-solid fa-circle-nodes'
        };

        const repostsAttribute: AttributeModel = {
            id: EngagementAttributeTypesEnum.shares,
            label: 'Mention.details.retweets',
            value: this.mention.share,
            type: attributeTypesEnum.engagement,
            icon: 'fa-solid fa-arrows-retweet'
        };

        const viewsAttribute: AttributeModel = {
            id: EngagementAttributeTypesEnum.views,
            label: 'Mention.details.views',
            value: this.mention.view,
            type: attributeTypesEnum.engagement,
            icon: 'fa-solid fa-eye'
        };

        const quotesAttribute: AttributeModel = {
            id: EngagementAttributeTypesEnum.quotes,
            label: 'Mention.details.quotes',
            value: this.mention.quote,
            type: attributeTypesEnum.engagement,
            icon: 'fa-solid fa-quotes'
        };

        this.engagementInformation = [
            engagementAttribute,
            engagementRateAttribute,
        ];

        switch (this.mention.mediaType.id) {
            case MediaTypeEnum.TWITTER:
                this.engagementInformation.push(
                    repostsAttribute,
                    quotesAttribute,
                    repliesAttribute,
                    likesAttribute,
                    viewsAttribute
                );
                break;
            case MediaTypeEnum.INSTAGRAM:
                this.engagementInformation.push(
                    sharesAttribute,
                    commentsAttribute,
                    likesAttribute
                );
                break;
            case MediaTypeEnum.REDDIT:
                this.engagementInformation.push(
                    sharesAttribute,
                    commentsAttribute,
                    upvotesAttribute,
                    viewsAttribute
                );
                break;
            case MediaTypeEnum.LINKEDIN:
                this.engagementInformation.push(
                    commentsAttribute,
                    likesAttribute
                );
                break;
            case MediaTypeEnum.BLUESKY:
                this.engagementInformation.push(
                    likesAttribute,
                    quotesAttribute,
                    repliesAttribute,
                    repostsAttribute
                );
                break;
            default:
                this.engagementInformation.push(
                    sharesAttribute,
                    commentsAttribute,
                    likesAttribute,
                    viewsAttribute
                );
                break;
        }
    }

    setMentionInformationAttributes(): void {
        if (this.regionRequest) {
            this.regionRequest.unsubscribe();
        }

        this.hasExpiryDateAttribute = false;

        this.mentionInformation = this.mentionsService.getMentionInformation(this.mention);
        this.regionRequest = this.mentionsService.getMentionRegion(this.mention)
            .subscribe((region) => {
                this.addLocationAttribute(region);
            });
    }

    private addLocationAttribute(region: JournodexGeographyModel): void {
        if (this.regionRequest) {
            this.regionRequest.unsubscribe();
            this.regionRequest = null;
        }

        let regionKeyString = 'na';

        if (region) {
            regionKeyString = region.displayString;
        }

        const locationAttribute: AttributeModel = {
            id: mentionAttributeTypesEnum.location,
            label: 'Mention.details.location',
            value: regionKeyString,
            type: attributeTypesEnum.simple,
            icon: 'fa-solid fa-location-dot',
            isMultiline: true
        };

        if (!this.hasExpiryDateAttribute) {
            this.mentionInformation = [
                ...this.mentionInformation,
                locationAttribute
            ];

            return;
        }

        this.mentionInformation.splice(-1, 0, locationAttribute);
    }

    setMentionProminenceAttributes(): void {
        this.mentionProminence = [];
        if (this.mention.prominenceScores.length > 0) {
            let prom: AttributeModel;
            this.mention.prominenceScores.forEach(score => {
                if (score.prominenceLevel !== ProminenceLevel.NONE) {
                    // Extract the last word of the of the subject name so that we can
                    // wrap it with the tooltip icon to prevent icon orphaning by line breaks
                    // If the subject name is only one word then label is empty and the
                    // name is stored in customLabel
                    const label = score.prominenceGroupName.split(' ');
                    const lastWord = label.splice(label.length - 1);
                    prom = {
                        id: null,
                        label: label.join(' '),
                        value: ProminenceScoreModel.getProminenceLabel(score.prominenceLevel),
                        type: attributeTypesEnum.prominence,
                        iconToolTip: score.keywords.toString(),
                        customTemplate: this.prominenceAttributeTemplate,
                        customLabel: lastWord
                    };
                    this.mentionProminence.push(prom);
                }
            });
        }
    }

    /**
     * Adds an Attribute to mention information to display the expiry date of content
     */
    addExpiryDateAttribute(): void {
        if (
            !this.preferences
            || !this.preferences.timeZone
            || !this.mention
            || !Mention.isCriticalMention(this.mention)
            || !this
        ) {
            return;
        }

        this.hasExpiryDateAttribute = true;

        const timezone = this.preferences && this.preferences.timeZone;
        const publishedDateInPerferedTimezone = moment(new Date(this.mention.datePublished)).tz(timezone);
        const currentDateInPreferedTimezone = moment().tz(timezone);
        const expiryDateInPerferedTimezone = publishedDateInPerferedTimezone.add(this.totalValidDaysForCriticalMention, 'd');
        const dateDifferenceInDays = expiryDateInPerferedTimezone.diff(currentDateInPreferedTimezone, 'd');

        let expiryDate: string;

        if (dateDifferenceInDays <= 0) {
            expiryDate = this.localizationService.transform('Mention.details.expired');
        } else {
            const localizedDaysRemaining = this.localizationService.transform(
                'Mention.details.daysRemaining',
                { days: dateDifferenceInDays }
            );
            expiryDate = publishedDateInPerferedTimezone.format(this.expiryDateFormat) + ' ' + localizedDaysRemaining;
        }

        const exipryDateAttribute: AttributeModel = {
            id: mentionAttributeTypesEnum.location,
            label: 'Mention.details.expiryDate',
            value: expiryDate,
            type: attributeTypesEnum.simple,
            icon: 'fa-regular fa-circle-info',
            iconToolTip: 'Mention.details.expiryDateToolTip'
        };

        this.mentionInformation = [
            ...this.mentionInformation,
            exipryDateAttribute
        ];
    }

    addMentionToFolder(folder: Folder): void {
        if (this.mention) {
            this.folderService.saveMentionsToFolder([this.mention], folder).subscribe();
        }
    }

    /**
     * Handles change to the tags selected for a mention
     */
    tagsUpdated(tagIds: number[]): void {
        this.mention.tagIds = tagIds;
        const cachedTagIds = [...this.initialTagIds];

        const patch: PartialMentionApiModel = {
            id: this.mention.mentionId,
            tagIds: this.mention.tagIds
        };

        this.mentionsService.patchAndReturnMention(patch).pipe(
            catchError(err => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Monitor.tags.updateErrorHeading',
                    detail: 'Monitor.tags.updateErrorMessage'
                });

                return NEVER;
            }),
            switchMap((response) => {
                this.mentionsService.setFocusedMention(response);

                if (cachedTagIds.length < response.tagIds.length) {
                    const uncachedIds = response.tagIds.filter(id => !cachedTagIds.includes(id));
                    return this.tagsService.getTagsByIds(uncachedIds).pipe(
                        filter(x => !!x),
                        tap(tags => {
                            tags.forEach(tag => {
                                this.messageService.add({
                                    severity: messageSeverityEnum.success,
                                    summary: this.localizationService.transform('Monitor.tags.addedSuccessHeading', {
                                        tagName: tag.name
                                    }),
                                    detail: this.localizationService.transform('Monitor.tags.addedSuccessMessage', {
                                        tagName: tag.name
                                    })
                                });
                            });
                        }),
                        take(1)
                    );
                } else {
                    const unsavedTagIds = cachedTagIds.filter(id => !response.tagIds.includes(id));
                    return this.tagsService.getTagsByIds(unsavedTagIds).pipe(
                        filter(x => !!x),
                        tap(tags => {
                            tags.forEach(tag => {
                                this.messageService.add({
                                    severity: messageSeverityEnum.success,
                                    summary: this.localizationService.transform('Monitor.tags.removedSuccessHeading', {
                                        tagName: tag.name
                                    }),
                                    detail: this.localizationService.transform('Monitor.tags.removedSuccessMessage', {
                                        tagName: tag.name
                                    })
                                });
                            });
                        }),
                        take(1)
                    );
                }
            })
        ).subscribe();
    }

    /**
     * Tracks a mention as viewed in the api
     */
    trackMention(mention: Mention): void {
        // Only track mentions that have a mercuryId
        if (mention.id) {
            this.mentionsApi.trackMentionView(mention.convertToApiMention()).subscribe();
        }
    }

    setNoProminenceContactLink(): void {
        if (this.configurationService.brandGUIDIsBurrelles()) {
            this.noProminenceContactLink = 'mailto:inquiry@burrelles.com';
        } else {
            this.noProminenceContactLink = 'https://www.agilitypr.com/contact/';
        }
    }

    openProminenceTakeOverPanel(event: Event): void {
        event.stopPropagation();
        this.coreDialogService.open(ProminenceTakeOverPanelComponent, {
            inputs: {
                overlayContainer: this.overlayContainer,
                animationTrigger: new BehaviorSubject(
                    new FullProfileAnimationModel(
                        FullProfileDialogAnimationsEnum.slideInOutAnimation,
                        OverlayAnimationStateEnum.open
                    )
                )
            }
        });
    }

    updateInsight(insight: MentionInsightModel): void {
        this.persistInsights(insight.name, ResourceActionTypes.UPDATE);
    }

    deleteInsight(insight: MentionInsightModel): void {
        this.insights = this.insights.filter(item => item.id !== insight.id);
        if (this.insights.length === 1) {
            // if there is only one insight, by default, it is the preferred one
            this.insights[0].isPreferred = true;
        }

        this.persistInsights(insight.name, ResourceActionTypes.DELETE);
    }

    preferInsight(insight: MentionInsightModel): void{
        this.insights.forEach(item => {
            if (item.id === insight.id) {
                item.isPreferred = true;
            } else {
                item.isPreferred = false;
            }
        });

        this.persistInsights(insight.name, ResourceActionTypes.UPDATE);
    }

    persistInsights(name: string, actionType: ResourceActionTypes): void {
        const patch: PartialMentionApiModel = {
            id: this.mention.mentionId,
            insights: this.insights?.map(insight => insight.toApiModel())
        };

        this.mentionsService.updateMentionInsights(patch, name, actionType).pipe().subscribe();
    }

    createNewInsight() {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const ioModel =  {
            inputs: {
                animationTrigger: animationTrigger,
                mention: this.mention,
                initialTab: MentionWorkbenchTabIds.INSIGHTS
            }
        };

        this.coreDialogService.open(MentionWorkbenchDialogComponent, ioModel, this.injector);
    }

    openManageMentionOrganizationDialog() {
        this.coreDialogService.open(ManageMentionOrganizationDialogComponent, {
            inputs: {
                overlayContainer: this.overlayContainer,
                animationTrigger: new BehaviorSubject(
                    new FullProfileAnimationModel(
                        FullProfileDialogAnimationsEnum.slideInOutAnimation,
                        OverlayAnimationStateEnum.open
                    )
                )
            }
        });
    }

    ngOnDestroy() {
        this.subscriptions.forEach(
            subscription => subscription.unsubscribe()
        );

        if (this.regionRequest) {
            this.regionRequest.unsubscribe();
            this.regionRequest = null;
        }
    }
}
